import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useSupportTicketsTableContext } from "../table/support-tickets-table-context";
import { getCloseNavigationTarget } from "./close-navigation-target";

const SUPPORT_V2_DETAIL_ROUTE = "/stores/:storeId/support-v2/:supportTicketId";

/**
 * Hook that returns a callback to drive post-close navigation from the team's
 * "After closing ticket action" setting. The callback is a no-op when invoked
 * from anywhere other than the detail view of the ticket being closed (e.g.
 * table row menu, bulk actions), so non-detail closes stay put.
 */
export function useCloseTicketNavigation(): (
  supportTicketId: SupportTicketId,
) => void {
  const team = useRequiredContext(TeamContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { data, openSupportTicket } = useSupportTicketsTableContext();

  return useHandler((supportTicketId: SupportTicketId) => {
    const match = matchPath(SUPPORT_V2_DETAIL_ROUTE, location.pathname);
    if (match?.params.supportTicketId !== supportTicketId) {
      return;
    }

    const target = getCloseNavigationTarget({
      closeAction: team.settings.support?.closeAction,
      data,
      supportTicketId,
    });

    if (target.kind === "next") {
      openSupportTicket(target.nextSupportTicketId);
    } else if (target.kind === "table") {
      navigate(`/stores/${team._id}/support-v2${location.search}`);
    }
  });
}
